/**
 * Writing a message, and editing one.
 *
 * Both are the same control: a textarea that grows in steps, an `@` menu over
 * the room's membership, a `#` menu over the channels you are in, and Enter to
 * commit. Editing reuses it rather than opening a dialog, because a message
 * should be corrected where it sits.
 *
 * The shape follows `_design/sketches/Composer.dc.html`: one row at rest, a
 * formatting toolbar that arrives with the caret (and simply stays on a wide
 * window, as on Mac and iPad), a field that grows one line at a time and then
 * scrolls inside itself, and a character count that appears once the message
 * grows beyond one visual line or the server's limit comes into sight.
 *
 * Sending goes through `sendMessage` in the sync store, which is already
 * optimistic and already retries. Nothing here keeps a second copy of the
 * message: the composer clears, and the store owns what happens next.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ClipboardEvent, DragEvent, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactElement } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { useMembers } from '@/api/queries'
import { sendMessage, setTyping, useConnectionState, useSyncStore, refreshCachedMessage, resync } from '@/api/store'
import { draftKey, loadDraft, saveDraft } from '@/api/drafts'
import type { Member, Room, Message } from '@/api/types'
import { Avatar, Button, HashIcon, IconButton, LockIcon, Modal, TextField } from '@/components/ui'
import { UploadTray, useUploads } from '@/components/upload'
import { Markdown } from '@/components/Markdown'
import { randomUuid } from '@/lib/uuid'
import styles from './Composer.module.css'
import { CommandSuggestions } from './CommandSuggestions'
import { GiphyButton, GiphyPicker } from './Giphy'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/** jsdom and older embedded web views do not implement this legacy editing API. */
function editorCommandState(command: string): boolean {
  return typeof document.queryCommandState === 'function' && document.queryCommandState(command)
}

function executeEditorCommand(command: string, value?: string): boolean {
  if (typeof document.execCommand !== 'function') return false
  document.execCommand(command, false, value)
  return true
}

/** At most one signal per interval, and one "stopped" once the typing pauses. */
const TYPING_INTERVAL_MS = 4_000
const TYPING_IDLE_MS = 6_000
const SUGGESTION_LIMIT = 8

/**
 * The cap the server enforces, for the older servers that do not publish
 * `message_max_length`. Being wrong here is harmless: the count is a courtesy,
 * and `POST /messages` is the authority either way.
 */
const FALLBACK_MAX_LENGTH = 16_000
/** Where the counter appears. Below this the field says nothing about length. */
const COUNTER_FROM = 0.9

/**
 * The heights the field is allowed to have, in lines. Two lines fit without
 * one line is added as needed, up to the device max, after which the field
 * scrolls inside itself rather than eating the conversation.
 */
const DESKTOP_MAX_ROWS = 15
const MOBILE_MAX_ROWS = 7
const DESKTOP_QUERY = '(min-width: 901px)'

function getComposerMaxRows(): number {
  if (typeof window === 'undefined') return DESKTOP_MAX_ROWS
  return window.matchMedia(DESKTOP_QUERY).matches ? DESKTOP_MAX_ROWS : MOBILE_MAX_ROWS
}

/**
 * How tall the field may grow without pushing it past the window's bottom.
 *
 * The dock is the last thing in a column that has already given the history
 * every pixel it can spare, so once the history is down to nothing the field is
 * the only thing left that can stop growing. Measured with the field at its
 * natural height, so the answer does not depend on the height being set.
 */
function spaceForField(
  field: HTMLElement,
  composer: HTMLElement | null,
  windowHeight: number,
): number {
  if (composer === null) return Number.POSITIVE_INFINITY
  const fieldBox = field.getBoundingClientRect()
  const under = Math.max(0, composer.getBoundingClientRect().bottom - fieldBox.bottom)
  return windowHeight - fieldBox.top - under
}

const EMOJIS = [
  '😀',
  '😂',
  '🥰',
  '👍',
  '👎',
  '👏',
  '🎉',
  '✅',
  '🚀',
  '💡',
  '🔥',
  '❤️',
  '🙌',
  '🤔',
  '👀',
  '😄',
  '🙏',
  '💬',
] as const

const MENTION_PATTERN = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u
// Channel slugs are lowercase and hyphenated, so the menu opens on the same
// characters the server would accept for a name.
const CHANNEL_PATTERN = /(?:^|\s)#([a-z0-9-]*)$/iu

/** The table picker never offers more columns than a phone can read. */
const TABLE_MAX_COLUMNS = 4

interface SelectionRange {
  start: number
  end: number
}

function wrapInlineMarkdown(children: string, marker: string): string {
  const leading = children.match(/^\s*/u)?.[0] ?? ''
  const trailing = children.match(/\s*$/u)?.[0] ?? ''
  const contentEnd = children.length - trailing.length
  const content = children.slice(leading.length, contentEnd)

  if (content === '') return children
  return `${leading}${marker}${content}${marker}${trailing}`
}

function serializePreContent(node: Node): string {
  if (node.nodeType === 3) return node.nodeValue ?? ''
  if (!(node instanceof HTMLElement)) {
    return Array.from(node.childNodes).map(serializePreContent).join('')
  }
  if (node.tagName.toLowerCase() === 'br') return '\n'
  return Array.from(node.childNodes).map(serializePreContent).join('')
}

function fencedCode(content: string): string {
  const longestRun = Math.max(0, ...Array.from(content.matchAll(/`+/gu), (match) => match[0].length))
  const fence = '`'.repeat(Math.max(3, longestRun + 1))
  return `${fence}\n${content}\n${fence}`
}

function cleanEditableFragment(markup: string): DocumentFragment {
  const parsed = new DOMParser().parseFromString(markup, 'text/html')

  for (const renderedPre of Array.from(parsed.body.querySelectorAll('pre'))) {
    const cleanPre = document.createElement('pre')
    cleanPre.textContent = renderedPre.querySelector('code')?.textContent ?? renderedPre.textContent ?? ''

    const renderedWrapper = renderedPre.parentElement
    if (renderedWrapper !== null && renderedWrapper.querySelector('button') !== null) {
      renderedWrapper.replaceWith(cleanPre)
    } else {
      renderedPre.replaceWith(cleanPre)
    }
  }

  // The message renderer dresses a table in its own scroller — border, radius
  // and shadow — and the composer dresses the table itself the same way. Both
  // at once draws one table as two nested boxes, so inside the editor the
  // renderer's classes come off and the wrapper keeps only its scrolling.
  for (const renderedTable of Array.from(parsed.body.querySelectorAll('table'))) {
    // Only the table's own frame. Inline markup in a cell — a mention, a link,
    // a piece of code — keeps the classes that draw it.
    const structure = renderedTable.querySelectorAll('thead,tbody,tfoot,tr,th,td')
    for (const element of [renderedTable, ...Array.from(structure)]) {
      element.removeAttribute('class')
    }

    const wrapper = renderedTable.parentElement
    if (wrapper !== null && wrapper.tagName === 'DIV' && wrapper.children.length === 1) {
      wrapper.className = styles.tableWrap ?? ''
    }
  }

  const fragment = document.createDocumentFragment()
  for (const child of Array.from(parsed.body.childNodes)) {
    fragment.append(document.importNode(child, true))
  }
  return fragment
}

function normalBlockAfter(block: HTMLElement): HTMLElement {
  const next = block.nextElementSibling
  if (
    next instanceof HTMLElement &&
    (next.tagName.toLowerCase() === 'p' || next.tagName.toLowerCase() === 'div') &&
    next.textContent === ''
  ) {
    return next
  }

  const normal = document.createElement('div')
  normal.append(document.createElement('br'))
  block.insertAdjacentElement('afterend', normal)
  return normal
}

function placeCaretAtStart(element: HTMLElement): void {
  const range = document.createRange()
  range.selectNodeContents(element)
  range.collapse(true)
  const currentSelection = window.getSelection()
  currentSelection?.removeAllRanges()
  currentSelection?.addRange(range)
}

function safeComposerHref(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null

  const candidate = /^[a-z][a-z0-9+.-]*:/iu.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(candidate)
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:'
      ? url.href
      : null
  } catch {
    return null
  }
}

function childTextNodes(node: Node): Text[] {
  const nodes: Text[] = []
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) nodes.push(child as Text)
    else nodes.push(...childTextNodes(child))
  }
  return nodes
}

function editableSelection(root: HTMLElement): SelectionRange | null {
  const current = window.getSelection()
  if (current === null || current.rangeCount === 0) return null

  const range = current.getRangeAt(0)
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null

  const start = document.createRange()
  start.selectNodeContents(root)
  start.setEnd(range.startContainer, range.startOffset)

  const end = document.createRange()
  end.selectNodeContents(root)
  end.setEnd(range.endContainer, range.endOffset)

  return { start: start.toString().length, end: end.toString().length }
}

function setEditableSelection(root: HTMLElement, visibleStart: number, visibleEnd: number): void {
  const nodes = childTextNodes(root)
  const total = nodes.reduce((length, node) => length + (node.nodeValue?.length ?? 0), 0)
  const startOffset = Math.max(0, Math.min(visibleStart, total))
  const endOffset = Math.max(startOffset, Math.min(visibleEnd, total))

  let cursor = 0
  let startNode = nodes[nodes.length - 1] ?? root
  let endNode = startNode
  let startAt = startNode === root ? 0 : startNode.nodeValue?.length ?? 0
  let endAt = startAt

  for (const node of nodes) {
    const length = node.nodeValue?.length ?? 0
    if (startNode !== root && startOffset >= cursor && startOffset <= cursor + length) {
      startNode = node
      startAt = startOffset - cursor
    }
    if (endOffset >= cursor && endOffset <= cursor + length) {
      endNode = node
      endAt = endOffset - cursor
      break
    }
    cursor += length
  }

  const range = document.createRange()
  range.setStart(startNode, startAt)
  range.setEnd(endNode, endAt)
  const current = window.getSelection()
  current?.removeAllRanges()
  current?.addRange(range)
}

function rawOffsetAtVisibleOffset(raw: string, visible: string, offset: number): number {
  let rawIndex = 0
  let visibleIndex = 0

  while (rawIndex < raw.length && visibleIndex < offset) {
    if (raw.charAt(rawIndex) === visible.charAt(visibleIndex)) visibleIndex += 1
    rawIndex += 1
  }

  return rawIndex
}

function visibleOffsetAtRawOffset(raw: string, visible: string, offset: number): number {
  let rawIndex = 0
  let visibleIndex = 0

  while (rawIndex < raw.length && rawIndex < offset) {
    if (raw.charAt(rawIndex) === visible.charAt(visibleIndex)) visibleIndex += 1
    rawIndex += 1
  }

  return Math.min(visibleIndex, visible.length)
}

function serializeEditable(node: Node): string {
  if (node.nodeType === 3) return node.nodeValue ?? ''
  if (!(node instanceof HTMLElement)) {
    return Array.from(node.childNodes).map(serializeEditable).join('')
  }

  const tag = node.tagName.toLowerCase()
  const children = Array.from(node.childNodes).map(serializeEditable).join('')

  if (tag === 'br') return '\n'
  if (tag === 'strong' || tag === 'b') return wrapInlineMarkdown(children, '**')
  if (tag === 'em' || tag === 'i') return wrapInlineMarkdown(children, '*')
  if (tag === 'u') return wrapInlineMarkdown(children, '++')
  if (tag === 'code') return `\`${children}\``
  if (tag === 'a') return `[${children}](${node.getAttribute('href') ?? ''})`
  if (tag === 'pre') return `${fencedCode(serializePreContent(node))}\n\n`

  if (tag === 'ul' || tag === 'ol') {
    const list = Array.from(node.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI')
      .map((item, index) => `${tag === 'ol' ? `${index + 1}. ` : '- '}${serializeEditable(item)}`)
      .join('\n')
    return `${list}\n\n`
  }

  if (tag === 'table') {
    const allRows = Array.from(node.querySelectorAll('tr'))
    const rows = allRows
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('th,td')).map((cell) => cell.textContent ?? '')
        return `| ${cells.join(' | ')} |`
      })
    const bodyRows = rows.slice(1).filter((_row, index) => (allRows[index + 1]?.textContent ?? '').trim() !== '')
    if (rows.length === 0) return ''

    const columns = node.querySelector('tr')?.querySelectorAll('th,td').length ?? 1
    const divider = `| ${Array.from({ length: columns }, () => '---').join(' | ')} |`
    return `${[rows[0], divider, ...bodyRows].join('\n')}\n\n`
  }

  if (tag === 'blockquote') {
    return `${children.split('\n').map((line) => `> ${line}`).join('\n')}\n\n`
  }
  if (tag === 'p') return `${children}\n\n`
  if (tag === 'div') {
    if (node.hasAttribute('contenteditable')) return children
    return `${children}\n`
  }
  return children
}

type SuggestionKind = 'user' | 'channel'

interface Suggestion {
  key: string
  /** What replaces the trigger and the query, without the leading sigil. */
  handle: string
  name: string
  kind: SuggestionKind
  userId: string | null
  avatarUrl: string | null
  /** Channels only: a closed channel carries the padlock, not the hash. */
  privateChannel: boolean
}

interface TriggerState {
  kind: SuggestionKind
  /** Index of the sigil in the text, so the replacement knows what to cut. */
  start: number
  query: string
}

function personName(member: Member): string {
  const display = member.user.display_name.trim()
  return display === '' ? member.user.username : display
}

function channelName(room: Room): string {
  return room.slug ?? room.name ?? 'channel'
}

function matchTrigger(text: string, caret: number): TriggerState | null {
  const before = text.slice(0, caret)

  const mention = MENTION_PATTERN.exec(before)
  const mentionQuery = mention?.[1]
  if (mention !== null && mentionQuery !== undefined) {
    return { kind: 'user', start: caret - mentionQuery.length - 1, query: mentionQuery }
  }

  const channel = CHANNEL_PATTERN.exec(before)
  const channelQuery = channel?.[1]
  if (channel !== null && channelQuery !== undefined) {
    return { kind: 'channel', start: caret - channelQuery.length - 1, query: channelQuery }
  }

  return null
}

// ── Icons ───────────────────────────────────────────────────────────────────

function SendIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M2.5 8h11M8.5 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function AttachIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function CodeIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m6 3-4 5 4 5M10 3l4 5-4 5M9 2 7 14"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function LinkIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2-2a2.6 2.6 0 1 0-3.7-3.7l-.6.6M9.4 6.6a2.6 2.6 0 0 0-3.7 0l-2 2a2.6 2.6 0 1 0 3.7 3.7l.6-.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function TableIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2.2 6.6h11.6M6.6 6.6v6.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}

// ── The table picker ────────────────────────────────────────────────────────

interface TablePickerProps {
  id: string
  onPick: (columns: number) => void
}

/**
 * Pick only the table width. Inserting a table creates a header and one body
 * row; further rows are added from the inline table controls.
 */
function TablePicker({ id, onPick }: TablePickerProps): ReactElement {
  const [hover, setHover] = useState(2)

  return (
    <div className={styles.tablePicker} id={id} role="dialog" aria-label="Choose a table size">
      <div
        className={styles.tableGrid}
        style={{ gridTemplateColumns: `repeat(${TABLE_MAX_COLUMNS}, 1.25rem)` }}
      >
        {Array.from({ length: TABLE_MAX_COLUMNS }, (_unused, index) => {
          const column = index + 1
          return (
            <button
              key={column}
              type="button"
              className={cx(styles.tableCell, column <= hover && styles.tableCellInside)}
              aria-label={`${column} column table`}
              onMouseEnter={() => setHover(column)}
              onFocus={() => setHover(column)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(column)}
            />
          )
        })}
      </div>
      <p className={styles.tableHint}>
        {`${hover} columns · header + 1 row`}
        <span aria-hidden="true"> · </span>
        {`${TABLE_MAX_COLUMNS} columns at most`}
      </p>
    </div>
  )
}

// ── The composer ────────────────────────────────────────────────────────────

export interface ComposerEdit {
  id: string
  text: string
  onSave: (text: string) => Promise<void>
  onCancel: () => void
}

export interface ComposerProps {
  roomId: string
  /** Used in the placeholder and the accessible label. */
  roomName?: string
  /** Present when this composer is correcting an existing message in place. */
  edit?: ComposerEdit
  /** Root message this send is a reply to. Threads are one level deep. */
  replyTo?: string | null
  /** False when another composer on the page should keep the caret. */
  autoFocus?: boolean
  /** Blocks writing entirely — an archived room, or one this account cannot post in. */
  disabled?: boolean
  disabledReason?: string
}

export function Composer({
  roomId,
  roomName,
  edit,
  replyTo = null,
  autoFocus = true,
  disabled = false,
  disabledReason,
}: ComposerProps): ReactElement {
  const { user, server } = useAuth()
  const { data: members } = useMembers(roomId)
  const { rooms } = useSyncStore()
  const connection = useConnectionState()
  const uploads = useUploads(roomId)

  const editing = edit !== undefined
  const editId = edit?.id
  const editText = edit?.text
  const storageKey = draftKey(user?.id, roomId, replyTo)
  const [text, setText] = useState(() => edit?.text ?? loadDraft(storageKey))
  const [trigger, setTrigger] = useState<TriggerState | null>(null)
  const [highlighted, setHighlighted] = useState(0)
  const [dropping, setDropping] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [commandNotice, setCommandNotice] = useState<string | null>(null)
  const commandAttempt = useRef<{ fingerprint: string; id: string } | null>(null)
  const commandContext = useRef('')
  commandContext.current = `${server?.server_id}:${user?.id}:${roomId}`
  const commandsEnabled = server?.capabilities?.commands === true
  const [giphyOpen, setGiphyOpen] = useState(false)
  const savingRef = useRef(false)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [tableOpen, setTableOpen] = useState(false)
  const [tableActive, setTableActive] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkURL, setLinkURL] = useState('https://')
  const [linkError, setLinkError] = useState<string | null>(null)
  const [maxRows, setMaxRows] = useState(() => getComposerMaxRows())
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  )
  const [multiline, setMultiline] = useState(false)
  const [boldActive, setBoldActive] = useState(false)
  const [italicActive, setItalicActive] = useState(false)
  const [underlineActive, setUnderlineActive] = useState(false)
  const [codeBlockActive, setCodeBlockActive] = useState(false)

  /** The latest text, for effects that must not re-run on every keystroke. */
  const textRef = useRef(text)
  /** What was being written when an edit began, held until the edit ends. */
  const draftRef = useRef<string | null>(editing ? loadDraft(storageKey) : null)
  const composerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const dragDepthRef = useRef(0)
  const tableSelectionRef = useRef<Range | null>(null)
  const editorDomTextRef = useRef<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const linkRangeRef = useRef<SelectionRange | null>(null)
  const selectionRef = useRef<SelectionRange | null>(null)
  const boldRangeRef = useRef<{ start: number } | null>(null)
  const lastTypingRef = useRef(0)
  const idleTimerRef = useRef<number | null>(null)

  const inputId = `composer-${roomId}${editing ? '-edit' : ''}${replyTo === null ? '' : `-thread-${replyTo}`}`
  const listId = `${inputId}-suggestions`

  const maxLength = server?.message_max_length ?? FALLBACK_MAX_LENGTH
  const overLimit = text.length > maxLength
  const showCount = multiline || text.length >= Math.floor(maxLength * COUNTER_FROM)

  // Keep the new-message draft separate from the message being edited. A
  // composer mounted in edit mode has no previous draft to restore.
  useLayoutEffect(() => {
    setSaveError(null)
    editorDomTextRef.current = null
    if (editText !== undefined) {
      if (draftRef.current === null) draftRef.current = textRef.current
      setText(editText)
    } else if (draftRef.current !== null) {
      setText(draftRef.current)
      draftRef.current = null
    }
  }, [editId, editText])

  useLayoutEffect(() => {
    const element = editorRef.current
    if (element === null || editorDomTextRef.current === text) return

    element.replaceChildren()
    if (text !== '') {
      element.append(
        cleanEditableFragment(
          renderToStaticMarkup(
            <Markdown
              text={text}
              currentUsername={user?.username ?? null}
              className={styles.editorMarkdown}
            />,
          ),
        ),
      )

      // Markdown renderers discard trailing spaces, but the composer needs
      // them to remain editable (for example after accepting #channel).
      const rawTrailing = /[ \t]+$/u.exec(text)?.[0] ?? ''
      const visibleTrailing = /[ \t]+$/u.exec(element.textContent ?? '')?.[0] ?? ''
      if (rawTrailing.length > visibleTrailing.length) {
        element.append(document.createTextNode(rawTrailing.slice(visibleTrailing.length)))
      }
    }
    for (const codeBlock of Array.from(element.querySelectorAll('pre'))) normalBlockAfter(codeBlock)
    editorDomTextRef.current = text
  }, [text, user?.username, editId, editText])

  useLayoutEffect(() => {
    textRef.current = text
  }, [text])

  useEffect(() => {
    if (!editing) saveDraft(storageKey, text)
  }, [editing, storageKey, text])

  // ── One-line growth ──────────────────────────────────────────────────────

  /*
   * Sets the field's height, and the scrollbar that goes with it, from what is
   * in it right now. Driven by the text below, but callable on its own: a
   * change to the editor's DOM does not always change the markdown it
   * serialises to, and the height still has to follow.
   */
  const fitEditor = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    const style = window.getComputedStyle(element)
    const lineHeight = Number.parseFloat(style.lineHeight)
    const padding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom)

    // jsdom, and any browser with a keyword line-height, gives no usable
    // number. Fall back to the old behaviour rather than guessing a height.
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      element.style.height = 'auto'
      element.style.height = `${element.scrollHeight}px`
      setMultiline(text.includes('\n'))
      return
    }

    element.style.height = 'auto'

    // New messages and edits share the same row limit. Keep an edit within
    // the viewport when loading a long existing message into the field.
    const styleCeiling = Number.parseFloat(style.maxHeight)
    const minimumHeight = lineHeight + padding
    const maximumHeight = Math.max(
      minimumHeight,
      Math.min(
        maxRows * lineHeight + padding,
        Number.isFinite(styleCeiling) ? styleCeiling : Number.POSITIVE_INFINITY,
        editing
          ? spaceForField(element, composerRef.current, viewportHeight)
          : Number.POSITIVE_INFINITY,
      ),
    )

    const neededHeight = Math.max(minimumHeight, element.scrollHeight)
    const neededRows = Math.max(1, Math.round((element.scrollHeight - padding) / lineHeight))
    setMultiline(neededRows > 1)
    // The one number that caps the height also decides the scrollbar. Two
    // different numbers leave everything between them clipped and out of reach.
    element.style.overflowY = neededHeight > maximumHeight ? 'auto' : 'hidden'
    element.style.height = `${Math.min(neededHeight, maximumHeight)}px`
  }, [text, maxRows, editing, viewportHeight])

  useLayoutEffect(() => {
    fitEditor()
  }, [fitEditor, showCount])

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_QUERY)
    const onResize = (): void => {
      setMaxRows(mediaQuery.matches ? DESKTOP_MAX_ROWS : MOBILE_MAX_ROWS)
      // A shorter window leaves the field less room, so the height is measured
      // again rather than staying at what the taller window allowed.
      setViewportHeight(window.innerHeight)
    }

    onResize()
    mediaQuery.addEventListener('change', onResize)
    window.addEventListener('resize', onResize)
    return () => {
      mediaQuery.removeEventListener('change', onResize)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // Selection placement after inserting formatting, emoji or a mention. It is
  // applied once the textarea has received its new controlled value.
  useLayoutEffect(() => {
    const pending = selectionRef.current
    if (pending === null) return
    selectionRef.current = null
    const element = editorRef.current
    if (element === null) return
    const visible = element.textContent ?? ''
    setEditableSelection(
      element,
      visibleOffsetAtRawOffset(text, visible, pending.start),
      visibleOffsetAtRawOffset(text, visible, pending.end),
    )
    element.focus()
  }, [text])

  // Opening a room should leave the cursor where the user is about to type.
  useEffect(() => {
    if (editing) editorRef.current?.focus()
  }, [editing])

  useEffect(() => {
    if (editing || disabled || !autoFocus) return
    editorRef.current?.focus()
  }, [autoFocus, roomId, editing, disabled, replyTo])

  // ── Typing signals ────────────────────────────────────────────────────────

  const stopTyping = useCallback((): void => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
    if (lastTypingRef.current !== 0) {
      lastTypingRef.current = 0
      setTyping(roomId, false)
    }
  }, [roomId])

  const signalTyping = useCallback((): void => {
    if (editing) return

    const now = Date.now()
    if (now - lastTypingRef.current > TYPING_INTERVAL_MS) {
      lastTypingRef.current = now
      setTyping(roomId, true)
    }

    if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current)
    idleTimerRef.current = window.setTimeout(stopTyping, TYPING_IDLE_MS)
  }, [editing, roomId, stopTyping])

  useEffect(() => stopTyping, [stopTyping])

  // ── Suggestions ───────────────────────────────────────────────────────────

  const candidates = useMemo<Suggestion[]>(() => {
    if (trigger === null) return []
    const needle = trigger.query.toLowerCase()

    if (trigger.kind === 'channel') {
      // Only channels the reader is in. A channel they are not a member of is
      // found through "Browse channels", where joining comes first — and a
      // private one they cannot see must not be completable from here at all.
      return rooms
        .filter((room) => room.kind === 'channel' && room.my_membership !== null)
        .filter((room) => room.archived_at === null)
        .map<Suggestion>((room) => ({
          key: room.id,
          handle: channelName(room),
          name: room.topic ?? '',
          kind: 'channel',
          userId: null,
          avatarUrl: null,
          privateChannel: room.visibility === 'private',
        }))
        .filter((candidate) => needle === '' || candidate.handle.toLowerCase().includes(needle))
        .sort((left, right) => left.handle.localeCompare(right.handle))
        .slice(0, SUGGESTION_LIMIT)
    }

    const people = (members ?? [])
      .filter((member) => member.user.id !== user?.id)
      .map<Suggestion>((member) => ({
        key: member.user.id,
        handle: member.user.is_local ? member.user.username : member.user.qualified_id,
        name: personName(member),
        kind: 'user',
        userId: member.user.id,
        avatarUrl: member.user.avatar_url,
        privateChannel: false,
      }))
      .filter(
        (candidate) =>
          needle === '' ||
          candidate.handle.toLowerCase().includes(needle) ||
          candidate.name.toLowerCase().includes(needle),
      )

    const everyone: Suggestion = {
      key: 'everyone',
      handle: 'everyone',
      name: 'Everyone',
      kind: 'user',
      userId: null,
      avatarUrl: null,
      privateChannel: false,
    }

    const withEveryone = 'everyone'.startsWith(needle) ? [everyone, ...people] : people
    return withEveryone.slice(0, SUGGESTION_LIMIT)
  }, [trigger, members, rooms, user])

  const closeSuggestions = useCallback((): void => {
    setTrigger(null)
    setHighlighted(0)
  }, [])

  const applyText = useCallback((value: string, caret: number): void => {
    setText(value)
    setTrigger(matchTrigger(value, caret))
    setHighlighted(0)
  }, [])

  const accept = useCallback(
    (candidate: Suggestion | undefined): void => {
      if (candidate === undefined || trigger === null) return

      const element = editorRef.current
      const visible = element?.textContent ?? ''
      const current = element === null ? null : editableSelection(element)
      const caret =
        current === null ? text.length : rawOffsetAtVisibleOffset(text, visible, current.start)
      const sigil = trigger.kind === 'channel' ? '#' : '@'
      const inserted = `${sigil}${candidate.handle} `
      const next = `${text.slice(0, trigger.start)}${inserted}${text.slice(caret)}`
      const at = trigger.start + inserted.length

      selectionRef.current = { start: at, end: at }
      setText(next)
      closeSuggestions()
    },
    [trigger, text, closeSuggestions],
  )

  // ── Formatting, emoji and tables ──────────────────────────────────────────

  const selection = useCallback((): SelectionRange => {
    const element = editorRef.current
    if (element === null) return { start: text.length, end: text.length }
    const current = editableSelection(element)
    if (current === null) return { start: text.length, end: text.length }

    const visible = element.textContent ?? ''
    return {
      start: rawOffsetAtVisibleOffset(text, visible, current.start),
      end: rawOffsetAtVisibleOffset(text, visible, current.end),
    }
  }, [text])

  const syncEditor = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    const visible = element.textContent ?? ''
    const current = editableSelection(element) ?? { start: visible.length, end: visible.length }
    const next = serializeEditable(element)
      .replace(/\u00a0/gu, ' ')
      .replace(/^\n+|\n+$/gu, '')
    const start = rawOffsetAtVisibleOffset(next, visible, current.start)

    editorDomTextRef.current = next
    setText(next)
    setBoldActive(editorCommandState('bold'))
    setItalicActive(editorCommandState('italic'))
    setUnderlineActive(editorCommandState('underline'))
    const anchor = window.getSelection()?.anchorNode
    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement
    setCodeBlockActive(Boolean(anchorElement?.closest('pre')))
    setTrigger(matchTrigger(next, start))
    setHighlighted(0)
  }, [])

  const updateTableActive = useCallback((): void => {
    const element = editorRef.current
    const anchor = window.getSelection()?.anchorNode
    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement
    setTableActive(Boolean(element !== null && anchorElement != null && element.contains(anchorElement) && anchorElement.closest('table')))
  }, [])

  const tableFromSelection = useCallback((): HTMLTableElement | null => {
    const element = editorRef.current
    const anchor = window.getSelection()?.anchorNode
    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement
    const table = anchorElement?.closest('table')
    return element !== null && table instanceof HTMLTableElement && element.contains(table) ? table : element?.querySelector('table') ?? null
  }, [])

  const addTableRow = useCallback((): void => {
    const table = tableFromSelection()
    if (table === null) return
    const columns = table.tHead?.rows[0]?.cells.length ?? table.rows[0]?.cells.length ?? 1
    const row = table.tBodies[0]?.insertRow()
    if (row === undefined) return
    for (let index = 0; index < columns; index += 1) row.insertCell().append(document.createElement('br'))
    syncEditor()
    // An empty row serialises to nothing, so `text` is unchanged and no render
    // follows. The field is a row taller all the same.
    fitEditor()
  }, [fitEditor, syncEditor, tableFromSelection])

  const removeTableRow = useCallback((): void => {
    const table = tableFromSelection()
    const body = table?.tBodies[0]
    if (body === undefined || body.rows.length <= 1) return
    body.deleteRow(body.rows.length - 1)
    syncEditor()
    fitEditor()
  }, [fitEditor, syncEditor, tableFromSelection])

  const replaceSelection = useCallback(
    (range: SelectionRange, replacement: string, nextSelection: SelectionRange): void => {
      const next = `${text.slice(0, range.start)}${replacement}${text.slice(range.end)}`
      selectionRef.current = nextSelection
      applyText(next, nextSelection.end)
      signalTyping()
    },
    [applyText, signalTyping, text],
  )

  const closeMenus = useCallback((): void => {
    setEmojiOpen(false)
    setTableOpen(false)
  }, [])

  const toggleBold = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    const current = editableSelection(element)
    const hasSelection = current !== null && current.start !== current.end
    element.focus()
    if (!executeEditorCommand('bold')) {
      const range = selection()
      const selected = text.slice(range.start, range.end)
      replaceSelection(range, `**${selected}**`, {
        start: range.start + 2,
        end: range.start + 2 + selected.length,
      })
      closeMenus()
      return
    }
    if (hasSelection) syncEditor()
    setBoldActive(editorCommandState('bold'))
    boldRangeRef.current = null
    closeMenus()
  }, [closeMenus, replaceSelection, selection, syncEditor, text])

  const toggleItalic = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    element.focus()
    if (!executeEditorCommand('italic')) {
      const range = selection()
      const selected = text.slice(range.start, range.end)
      replaceSelection(range, `*${selected}*`, {
        start: range.start + 1,
        end: range.start + 1 + selected.length,
      })
      closeMenus()
      return
    }
    setItalicActive(editorCommandState('italic'))
    syncEditor()
    closeMenus()
  }, [closeMenus, replaceSelection, selection, syncEditor, text])

  const toggleUnderline = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    element.focus()
    if (!executeEditorCommand('underline')) {
      const range = selection()
      const selected = text.slice(range.start, range.end)
      replaceSelection(range, `++${selected}++`, {
        start: range.start + 2,
        end: range.start + 2 + selected.length,
      })
      closeMenus()
      return
    }
    setUnderlineActive(editorCommandState('underline'))
    syncEditor()
    closeMenus()
  }, [closeMenus, replaceSelection, selection, syncEditor, text])

  const toggleCodeBlock = useCallback((): void => {
    const element = editorRef.current
    if (element === null) return

    element.focus()
    const currentSelection = window.getSelection()
    const anchor = currentSelection?.anchorNode
    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement
    const codeBlock = anchorElement?.closest('pre')

    if (codeBlock instanceof HTMLElement) {
      const normal = normalBlockAfter(codeBlock)
      placeCaretAtStart(normal)
      setCodeBlockActive(false)
      syncEditor()
      closeMenus()
      return
    }

    if (!executeEditorCommand('formatBlock', 'pre')) {
      const range = selection()
      const selected = text.slice(range.start, range.end)
      const replacement = `\n\n${fencedCode(selected)}\n\n`
      replaceSelection(range, replacement, {
        start: range.start + 5,
        end: range.start + 5 + selected.length,
      })
      setCodeBlockActive(true)
      closeMenus()
      return
    }
    const nextAnchor = window.getSelection()?.anchorNode
    const nextAnchorElement = nextAnchor instanceof Element ? nextAnchor : nextAnchor?.parentElement
    const nextCodeBlock = nextAnchorElement?.closest('pre')
    if (nextCodeBlock instanceof HTMLElement) normalBlockAfter(nextCodeBlock)
    setCodeBlockActive(true)
    syncEditor()
    closeMenus()
  }, [closeMenus, replaceSelection, selection, syncEditor, text])

  const rememberTableSelection = useCallback((event: ReactMouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    const element = editorRef.current
    const currentSelection = window.getSelection()
    if (element === null || currentSelection === null || currentSelection.rangeCount === 0) return

    const range = currentSelection.getRangeAt(0)
    if (element.contains(range.commonAncestorContainer)) tableSelectionRef.current = range.cloneRange()
  }, [])

  const openLinkModal = useCallback((): void => {
    linkRangeRef.current = selection()
    setLinkURL('https://')
    setLinkError(null)
    closeMenus()
    setLinkOpen(true)
  }, [closeMenus, selection])

  const closeLinkModal = useCallback((): void => {
    setLinkOpen(false)
    setLinkError(null)
    linkRangeRef.current = null
  }, [])

  const applyLink = useCallback((): void => {
    const raw = linkURL.trim()
    const href = safeComposerHref(raw)
    if (href === null) {
      setLinkError('Enter a valid web or email address.')
      return
    }

    const range = linkRangeRef.current ?? { start: text.length, end: text.length }
    const selected = text.slice(range.start, range.end)
    const label = selected === '' ? raw : selected
    const replacement = `[${label}](${href})`
    replaceSelection(range, replacement, {
      start: range.start + 1,
      end: range.start + 1 + label.length,
    })
    setLinkOpen(false)
    setLinkError(null)
    linkRangeRef.current = null

    // Modal restores focus to its trigger as it closes. Return it to the
    // editor on the following task, after that restoration has completed.
    window.setTimeout(() => editorRef.current?.focus(), 0)
  }, [linkURL, replaceSelection, text])

  const insertTable = useCallback(
    (columns: number): void => {
      const element = editorRef.current
      if (element === null) return

      element.focus()
      const currentSelection = window.getSelection()
      const savedRange = tableSelectionRef.current
      if (currentSelection !== null && savedRange !== null) {
        currentSelection.removeAllRanges()
        currentSelection.addRange(savedRange)
      }

      const header = '<th><br></th>'.repeat(columns)
      const cells = '<td><br></td>'.repeat(columns)
      const body = `<tr>${cells}</tr>`
      if (
        !executeEditorCommand(
          'insertHTML',
          `<table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table><div><br></div>`,
        )
      ) {
        const range = selection()
        const headings = Array.from({ length: columns }, (_, index) => `Column ${index + 1}`)
        const markdown = [
          `| ${headings.join(' | ')} |`,
          `| ${headings.map(() => '---').join(' | ')} |`,
          `| ${headings.map(() => '').join(' | ')} |`,
        ].join('\n')
        replaceSelection(range, markdown, {
          start: range.start + 2,
          end: range.start + 2 + headings[0]!.length,
        })
      } else {
        syncEditor()
      }
      tableSelectionRef.current = null
      setTableActive(true)
      closeMenus()
    },
    [closeMenus, replaceSelection, selection, syncEditor],
  )

  const insertEmoji = useCallback(
    (emoji: string): void => {
      const range = selection()
      const position = range.start + emoji.length
      replaceSelection(range, emoji, { start: position, end: position })
      closeMenus()
    },
    [closeMenus, replaceSelection, selection],
  )

  // ── Sending ───────────────────────────────────────────────────────────────

  const trimmed = text.trim()
  const attachmentIds = uploads.readyIds
  // An unfinished tray holds back a new message, but never an edit: the edit
  // has nothing to do with the files waiting in it. A file that failed or was
  // cancelled counts as unfinished — sending would drop it without saying so,
  // and clearing the tray afterwards would take its error and its Retry button
  // with it.
  const heldByUploads = !editing && (uploads.busy || uploads.failed)
  const canSubmit =
    !disabled &&
    !heldByUploads &&
    !saving &&
    !overLimit &&
    (trimmed !== '' || (!editing && attachmentIds.length > 0))

  const submit = useCallback(async (): Promise<void> => {
    if (disabled || savingRef.current || text.length > maxLength) return
    if (!editing && (uploads.busy || uploads.failed)) return

    if (edit !== undefined) {
      if (trimmed === '' || trimmed === edit.text.trim()) {
        edit.onCancel()
        return
      }
      savingRef.current = true
      setSaving(true)
      setSaveError(null)
      try {
        await edit.onSave(trimmed)
      } catch (error) {
        setSaveError(isApiError(error) || error instanceof Error ? error.message : 'The message could not be saved. Try again.')
      } finally {
        savingRef.current = false
        setSaving(false)
      }
      return
    }

    if (trimmed === '' && attachmentIds.length === 0) return

    if (commandsEnabled && /^\/[a-z]+(?:\s|$)/.test(trimmed)) {
      const context = commandContext.current
      const fingerprint = JSON.stringify([context, trimmed, attachmentIds, replyTo])
      if (commandAttempt.current?.fingerprint !== fingerprint) commandAttempt.current = { fingerprint, id: randomUuid() }
      const clientMessageId = commandAttempt.current.id
      savingRef.current = true; setSaving(true); setSaveError(null); setCommandNotice(null)
      try {
        const result = await api.post<Message | { notice: string }>(`/rooms/${roomId}/commands`, {
          text: trimmed, client_message_id: clientMessageId, attachment_ids: attachmentIds,
          ...(replyTo ? { parent_id: replyTo } : {}),
        })
        if (context !== commandContext.current) return
        commandAttempt.current = null
        if ('notice' in result) { setCommandNotice(result.notice); void resync() }
        else refreshCachedMessage(result)
        setText(''); uploads.clear(); closeSuggestions(); stopTyping()
      } catch (error) {
        if (context === commandContext.current) setSaveError(error instanceof Error || isApiError(error) ? error.message : 'The command could not be run. Your draft is still here.')
      } finally { savingRef.current = false; setSaving(false) }
      return
    }

    const ids = [...attachmentIds]
    setText('')
    setBoldActive(false)
    boldRangeRef.current = null
    closeSuggestions()
    stopTyping()

    await sendMessage(roomId, commandsEnabled && trimmed.startsWith('//') ? trimmed.slice(1) : trimmed, ids, [], replyTo)

    // Cleared only once the send has been handed to the store, which owns the
    // retry from here on; the tray has nothing left to say about these files.
    uploads.clear()
  }, [
    commandsEnabled,
    attachmentIds,
    closeSuggestions,
    disabled,
    edit,
    editing,
    maxLength,
    replyTo,
    roomId,
    stopTyping,
    text.length,
    trimmed,
    uploads,
  ])

  // ── Keyboard ──────────────────────────────────────────────────────────────

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Enter confirms an IME candidate; it is not a send/save command.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return
    if ((event.metaKey || event.ctrlKey) && !event.altKey) {
      const shortcut = event.key.toLowerCase()
      if (shortcut === 'b' || shortcut === 'i' || shortcut === 'u') {
        event.preventDefault()
        if (shortcut === 'b') toggleBold()
        else if (shortcut === 'i') toggleItalic()
        else toggleUnderline()
        return
      }
    }

    if (trigger !== null && candidates.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlighted((current) => (current + 1) % candidates.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlighted((current) => (current - 1 + candidates.length) % candidates.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        accept(candidates[highlighted])
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeSuggestions()
        return
      }
    }

    if (event.key === 'Escape' && edit !== undefined) {
      event.preventDefault()
      event.stopPropagation()
      if (!savingRef.current) edit.onCancel()
      return
    }

    if (event.key === 'Escape' && (emojiOpen || tableOpen)) {
      event.preventDefault()
      closeMenus()
      editorRef.current?.focus()
      return
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      void submit()
    }
  }

  // ── Files ─────────────────────────────────────────────────────────────────

  const acceptFiles = useCallback(
    (files: FileList | null): void => {
      if (files === null || files.length === 0) return
      uploads.addFiles(files)
    },
    [uploads],
  )

  const onPaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    const files = event.clipboardData.files
    if (files.length > 0) {
      if (editing) return
      // Otherwise the browser pastes an image as a data URI into the text.
      event.preventDefault()
      acceptFiles(files)
      return
    }

    // Only the text. A rich clipboard — a web page, a document — would land
    // in the editor as HTML, with whatever markup came along for the ride,
    // and the composer's own formatting is the only formatting there is.
    event.preventDefault()
    const plain = event.clipboardData.getData('text/plain')
    if (plain === '') return

    if (executeEditorCommand('insertText', plain)) {
      syncEditor()
      signalTyping()
      return
    }

    const range = selection()
    const position = range.start + plain.length
    replaceSelection(range, plain, { start: position, end: position })
  }

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    if (editing) return
    event.preventDefault()
    dragDepthRef.current = 0
    setDropping(false)
    acceptFiles(event.dataTransfer.files)
  }

  const onDragEnter = (event: DragEvent<HTMLDivElement>): void => {
    if (editing || event.dataTransfer.types.indexOf('Files') === -1) return
    event.preventDefault()
    dragDepthRef.current += 1
    setDropping(true)
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>): void => {
    if (editing || event.dataTransfer.types.indexOf('Files') === -1) return
    event.preventDefault()
    setDropping(true)
  }

  const onDragLeave = (): void => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDropping(false)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (disabled && !editing) {
    return (
      <div className={styles.composer}>
        <p className={styles.notice}>{disabledReason ?? 'You cannot post in this room.'}</p>
      </div>
    )
  }

  const placeholder = editing
    ? 'Edit this message'
    : replyTo !== null
      ? 'Reply in thread'
      : roomName === undefined
        ? 'Write a message'
        : `Message ${roomName}`

  const activeDescendant =
    trigger !== null && candidates[highlighted] !== undefined ? `${listId}-${highlighted}` : undefined

  // The strip sits on the composer rather than across the window: the
  // conversation stays readable while it is up, and what it has to say is only
  // about what happens to the message you are writing.
  // `connecting` is the first moments after a reload, and saying anything then
  // would be a strip that flashes on every page load.
  const offline =
    !editing && (connection.status === 'offline' || connection.status === 'reconnecting')

  return (
    <div ref={composerRef} className={cx(styles.composer, editing && styles.inline)}>
      {giphyOpen ? <GiphyPicker onClose={() => setGiphyOpen(false)} onSelect={link => { setText(current => current.trim() ? `${current}\n${link}` : link); setGiphyOpen(false) }} /> : null}
      {!editing && server?.capabilities?.giphy ? <GiphyButton onOpen={() => setGiphyOpen(true)} /> : null}
      {!editing && commandsEnabled ? <CommandSuggestions text={text} onChoose={setText} /> : null}
      {commandNotice ? <p className={styles.notice} style={{ whiteSpace: 'pre-line' }} role="status">{commandNotice} <button type="button" onClick={() => setCommandNotice(null)}>Dismiss</button></p> : null}
      {trigger !== null && candidates.length > 0 ? (
        <ul
          className={styles.mentions}
          id={listId}
          role="listbox"
          aria-label={trigger.kind === 'channel' ? 'Channels' : 'People to mention'}
        >
          {candidates.map((candidate, index) => (
            <li
              key={candidate.key}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlighted}
              className={styles.mention}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={(event) => {
                // Keeps focus in the textarea so the caret survives the click.
                event.preventDefault()
                accept(candidate)
              }}
            >
              {candidate.kind === 'channel' ? (
                <span className={styles.channelGlyph} aria-hidden="true">
                  {candidate.privateChannel ? <LockIcon /> : <HashIcon />}
                </span>
              ) : (
                <Avatar
                  name={candidate.name}
                  id={candidate.userId ?? candidate.key}
                  src={candidate.avatarUrl}
                  size="xs"
                  decorative
                />
              )}
              <span className={styles.mentionName}>
                {candidate.kind === 'channel' ? candidate.handle : candidate.name}
              </span>
              <span className={styles.mentionHandle}>
                {candidate.kind === 'channel' ? candidate.name : `@${candidate.handle}`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div
        className={cx(styles.box, dropping && styles.dropping)}
        onDragEnter={onDragEnter}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {offline ? (
          <p className={styles.offline} role="status">
            Reconnecting… messages you write now are sent when the line is back.
          </p>
        ) : null}

        {!editing && uploads.items.length > 0 ? (
          <div className={styles.tray}>
            <UploadTray uploads={uploads} />
          </div>
        ) : null}

        <div className={styles.toolbar} role="toolbar" aria-label="Message formatting">
          {/* Everything but the paperclip scrolls sideways when the window is
              too narrow for the row; the paperclip keeps its place. */}
          <div className={styles.tools}>
    <IconButton
      className={styles.formatButton}
      label="Bold"
      size="sm"
      pressed={boldActive}
      aria-pressed={boldActive}
      onMouseDown={(event) => event.preventDefault()}
      onClick={toggleBold}
    >
      <strong className={styles.boldIcon}>B</strong>
    </IconButton>
            <IconButton
              className={styles.formatButton}
              label="Italic"
              size="sm"
              pressed={italicActive}
              aria-pressed={italicActive}
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleItalic}
            >
              <em className={styles.italicIcon}>I</em>
            </IconButton>
            <IconButton
              className={styles.formatButton}
              label="Underline"
              size="sm"
              pressed={underlineActive}
              aria-pressed={underlineActive}
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleUnderline}
            >
              <span className={styles.underlineIcon}>U</span>
            </IconButton>
            <IconButton
              className={styles.formatButton}
              label="Code block"
              size="sm"
              pressed={codeBlockActive}
              aria-pressed={codeBlockActive}
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleCodeBlock}
            >
              <CodeIcon />
            </IconButton>
            <IconButton
              className={styles.formatButton}
              label="Link"
              size="sm"
              onMouseDown={(event) => event.preventDefault()}
              onClick={openLinkModal}
            >
              <LinkIcon />
            </IconButton>
            <IconButton
              className={styles.formatButton}
              label="Table"
              size="sm"
              pressed={tableOpen}
              aria-expanded={tableOpen}
              aria-controls={tableOpen ? `${inputId}-table` : undefined}
              onMouseDown={rememberTableSelection}
              onClick={() => {
                setEmojiOpen(false)
                setTableOpen((open) => !open)
              }}
            >
              <TableIcon />
            </IconButton>
            {tableActive ? (
              <div className={styles.tableControls} role="group" aria-label="Edit table rows">
                <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={addTableRow}>
                  + row
                </button>
                <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={removeTableRow}>
                  − row
                </button>
              </div>
            ) : null}
          </div>

          {editing ? <span className={styles.editLabel} title="Enter to save, Escape to cancel.">Editing message</span> : null}

          <div className={styles.emojiTool}>
            <IconButton
              className={styles.emojiAction}
              label="Choose an emoji"
              size="sm"
              pressed={emojiOpen}
              aria-controls={emojiOpen ? `${inputId}-emojis` : undefined}
              aria-expanded={emojiOpen}
              onClick={() => {
                setTableOpen(false)
                setEmojiOpen((open) => !open)
              }}
            >
              <span className={styles.emojiIcon}>☺</span>
            </IconButton>

            {emojiOpen ? (
              <div
                id={`${inputId}-emojis`}
                className={styles.emojiPicker}
                role="dialog"
                aria-label="Choose an emoji"
              >
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    className={styles.emojiOption}
                    type="button"
                    aria-label={`Insert ${emoji}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {editing ? (
            <IconButton label="Cancel" size="sm" disabled={saving} onClick={edit.onCancel}>
              <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
                <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </IconButton>
          ) : (
            <>
              <input
                ref={fileInputRef}
                className="sr-only"
                type="file"
                multiple
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => {
                  acceptFiles(event.target.files)
                  event.target.value = ''
                }}
              />
              <IconButton
                className={styles.attach}
                label="Attach files"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachIcon />
              </IconButton>
            </>
          )}

          {tableOpen ? <TablePicker id={`${inputId}-table`} onPick={insertTable} /> : null}

        </div>

        <div className={styles.row}>
          {editing ? null : (
            <IconButton
              className={styles.restPlus}
              label="Attach files"
              size="sm"
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
            >
              <AttachIcon />
            </IconButton>
          )}
          <div className={styles.inputShell}>
            <div
              id={inputId}
              ref={editorRef}
              className={cx(styles.editorContent, showCount && styles.editorWithCount)}
              contentEditable={!disabled && !saving}
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              aria-label={editing ? 'Edit message' : placeholder}
              aria-autocomplete="list"
              aria-expanded={trigger !== null && candidates.length > 0}
              aria-controls={trigger !== null && candidates.length > 0 ? listId : undefined}
              aria-activedescendant={activeDescendant}
              aria-describedby={showCount ? `${inputId}-count` : undefined}
              aria-invalid={overLimit}
              data-placeholder={placeholder}
              spellCheck={false}
              onInput={syncEditor}
              onFocus={updateTableActive}
              onMouseUp={updateTableActive}
              onKeyUp={updateTableActive}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={stopTyping}
            />

            {showCount ? (
              <div className={styles.actions}>
                <span
                  id={`${inputId}-count`}
                  className={cx(styles.count, overLimit && styles.countOver)}
                  role={overLimit ? 'alert' : undefined}
                >
                  {`${text.length.toLocaleString()} / ${maxLength.toLocaleString()}`}
                </span>
              </div>
            ) : null}

            <IconButton
              className={styles.sendAction}
              label={editing ? 'Save' : 'Send message'}
              size="sm"
              variant="primary"
              loading={saving}
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              {editing ? (
                <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
                  <path d="m3 8 3.5 3.5L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : <SendIcon />}
            </IconButton>
          </div>
        </div>
        {saveError !== null ? <p className={styles.saveError} role="alert">{saveError}</p> : null}
      </div>

      {!editing && (
        <p className={styles.hint}>
          {overLimit ? (
            <span>This message is longer than the server accepts.</span>
          ) : uploads.busy ? (
            <span>Waiting for files to finish uploading…</span>
          ) : uploads.failed ? (
            <span>Retry or remove the files that did not upload, then send.</span>
          ) : null}
        </p>
      )}

      <Modal
        open={linkOpen}
        onClose={closeLinkModal}
        title="Add link"
        description="Paste a web or email address. Selected text becomes the link label."
        size="sm"
        closeOnOverlayClick={false}
        initialFocusRef={linkInputRef}
        footer={
          <>
            <Button onClick={closeLinkModal}>Cancel</Button>
            <Button variant="primary" type="submit" form={`${inputId}-link-form`}>
              Add link
            </Button>
          </>
        }
      >
        <form
          id={`${inputId}-link-form`}
          onSubmit={(event) => {
            event.preventDefault()
            applyLink()
          }}
        >
          <TextField
            ref={linkInputRef}
            label="Address"
            value={linkURL}
            inputMode="url"
            autoComplete="url"
            error={linkError}
            onChange={(event) => {
              setLinkURL(event.target.value)
              if (linkError !== null) setLinkError(null)
            }}
          />
        </form>
      </Modal>
    </div>
  )
}
